// Main hook using useSuspenseQuery - data is always available after Suspense resolves
import {useQueryClient, useSuspenseQuery, UseSuspenseQueryOptions} from "@tanstack/react-query";
import {AppData, AppDataMediaItem} from "@/data/app-data-interfaces";
import {appDataKeys} from "@/data/app-data-keys";
import {getAppData} from "@/data/app-data-api";
import {appConfig} from "@/app-config";
import {AxiosError} from "axios";
import {useMemo} from "react";

export const useAppData = (
    options?: Omit<UseSuspenseQueryOptions<AppData, AxiosError>, 'queryKey' | 'queryFn'>
) => {
    return useSuspenseQuery({
        queryKey: appDataKeys.detail(),
        queryFn: getAppData,
        staleTime: appConfig.appDataPersistence.staleTime,
        retry: 3,
        retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
        networkMode: 'online',
        ...options,
    });
};

export const useInvalidator = () => {
    const queryClient = useQueryClient();
    return {
        invalidateAppData: () => queryClient.invalidateQueries({queryKey: appDataKeys.all}),
    };
};

// Organization data hooks - all data is always available (no undefined checks needed)
export const useOrganizationData = (orgId: string) => {
    const {data: appData, ...queryResult} = useAppData();

    return {
        ...queryResult,
        data: appData[orgId], // Always defined after Suspense boundary
    };
};

export const useOrganizationMetaData = (orgId: string) => {
    const {data: appData, ...queryResult} = useAppData();

    return {
        ...queryResult,
        data: appData[orgId]?.organization, // Always defined after Suspense boundary
    };
};

export const useOrganizationAchievements = (orgId: string) => {
    const {data: appData, ...queryState} = useAppData();

    return {
        ...queryState,
        data: appData[orgId]?.achievements || [], // Keep optional chaining for orgId safety
    };
};

export const useOrganizationOffers = (orgId: string) => {
    const {data: appData, ...queryState} = useAppData();

    return {
        ...queryState,
        data: appData[orgId]?.offers || [], // Keep optional chaining for orgId safety
    };
};

export const useOrganizationMedia = (orgId: string) => {
    const {data: appData, ...queryState} = useAppData();

    const mediaItems = appData[orgId]?.media?.items;
    const sortedMedia = mediaItems
        ? Object.values(mediaItems).sort((a, b) => a.sorting - b.sorting)
        : [];

    return {
        ...queryState,
        data: sortedMedia,
    };
};

export const useFeaturedMedia = (orgId: string) => {
    const {data: appData, ...queryState} = useAppData();

    const orgData = appData[orgId];
    const featuredMedia = orgData
        ? orgData.media.featured
            .map(id => orgData.media.items[id])
            .filter(Boolean)
            .sort((a, b) => a.sorting - b.sorting)
        : [];

    return {
        ...queryState,
        data: featuredMedia,
    };
};

export const useMediaByType = (orgId: string, type: AppDataMediaItem['type']) => {
    const {data: mediaItems, ...queryState} = useOrganizationMedia(orgId);

    return {
        ...queryState,
        data: mediaItems.filter(item => item.type === type),
    };
};

export const useOrganizationMembership = (orgId: string) => {
    const {data: appData, ...queryState} = useAppData();

    return {
        ...queryState,
        data: appData[orgId]?.membership,
    };
};

export const useOrganizationFolders = (orgId: string) => {
    const {data: appData, ...queryState} = useAppData();

    return {
        ...queryState,
        data: appData[orgId]?.media?.folders || {},
    };
};


export const useMediaPath = (orgId: string, folderId: string) => {
    const {data, ...queryState} = useOrganizationData(orgId);
    const folders = data.media.folders;

    const buildFolderPath = (folderId: string | undefined): string[] => {
        if (!folderId || !folders[folderId]) {
            return ['Main'];
        }

        const folder = folders[folderId];
        const path = [folder.name];

        // Recursively build path if folder has a parent
        if (folder.parentId && folders[folder.parentId]) {
            const parentPath = buildFolderPath(folder.parentId);
            return [...parentPath, ...path];
        }

        return path;
    };

    return {
        ...queryState,
        data: buildFolderPath(folderId)
    };
};

/**
 * Hook that applies a selector function to cached AppData
 * This allows components to derive specific data from the AppData without refetching
 */
export function useAppDataSelector<T>(
    selectorFn: (appData: AppData | undefined) => T
): T {
    // Get the cached AppData (this should use your existing useAppData hook)
    const {data: appData} = useAppData();

    // Apply the selector function and memoize the result
    const selectedData = useMemo(() => {
        return selectorFn(appData);
    }, [appData, selectorFn]);

    return selectedData;
}
